/**
 * Here you can find methods for using react-route-dom navigation from WF components
 * This will help navigating through screens of WF and Toqio Core without the need of refreshing the whole web
 *   for connecting the methods between both sides we have to add them inside the object here:
 *   window.flowable.externals.additionalData
 */
import { isEmpty, isNull, isObject, isUndefined, omitBy } from 'lodash';
import CONST from 'utils/constants';
const { WORKFLOWS_STORE_KEYS } = CONST

// Encodes search params into url
const encodeSearch = (search: any) => {
  try {
    return search ? encodeURIComponent(JSON.stringify(omitBy(search, (v) => v === '' || isUndefined(v) || isNull(v) || (isEmpty(v) && isObject(v))))) : '';
  } catch (e) {
    console.error(e);
    return '';
  }
}
// Decode an url into search params
const decodeSearch = (search = '') => {
  try {
    return isEmpty(search) ? {} : JSON.parse(decodeURIComponent(search.slice(1)))
  } catch (e) {
    console.error(e);
    return {};
  }
}

// navigate to a new route and send params if existing any
const navigate = (url: string, params: any = {}) => {
  const storeMethods = window?.flowable?.externals?.additionalData?.store
  const history = storeMethods.get(WORKFLOWS_STORE_KEYS.HISTORY)

  // navigate with window.open when it is same WF app because if it is 2nd navigation it won't navigate properly
  // flowable will interprate it as a navigation inside the same app and won't refresh the page
  if (params?.sameApp) {
    window.open(url, '_self')
  }
  // navigate using history.push between other apps of WF or betwee WF and ToqioCore
  // to avoid refreshing the whole platform and fetching again everything
  else {
    history?.push?.(url, params)
  }
}

const replace = (extraUrl: string = '', params: any = {}) => {
  const storeMethods = window?.flowable?.externals?.additionalData?.store
  const history = storeMethods.get(WORKFLOWS_STORE_KEYS.HISTORY)
  let url = `#/${extraUrl}`;

  if (params?.currentPageId) {
    url = `#/${params.currentAppId}/${params.currentPageId}/${extraUrl}`
  }

  history?.replace?.(url)
}

// navigate back to the previous screen
const goBack = () => {
  const storeMethods = window?.flowable?.externals?.additionalData?.store
  const history = storeMethods.get(WORKFLOWS_STORE_KEYS.HISTORY)
  history?.goBack?.()
}

const open = (url: string, params: { target?: string; popUp?: boolean } = {}) => {
  const target = params?.target || "_blank";

  if (params?.popUp) {
    window.open(url, target, "popup=true")
  }
  else {
    window.open(url, target)
  }
}

// close the tab
const close = () => {
  window.close()
}

const getURLSearchParams = (): { hash: string; [key: string]: string | undefined } => {
  const hash = window?.location?.hash || '';
  const params = Object.fromEntries(
    new URLSearchParams(window?.location?.search || '')
  ) as Record<string, string>;

  return { hash, ...params };
};

const setQueryParams = (queryName: string, params: any = {}, existingParams: any = {}) => {
  const url = `?${queryName}=${encodeSearch(params)}`;
  replace(url, existingParams)
}

const getQueryParams = (url: string) => {
  return decodeSearch(`?${url}`);
}

export const navigationMethods = {
  navigate,
  replace,
  goBack,
  open,
  close,
  setQueryParams,
  getQueryParams,
  getURLSearchParams
}